import { useState, useMemo, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Formik } from 'formik';
import sortBy from 'lodash/sortBy';

import FORMATTERS from 'helpers/formatters';
import { FloatingCard, Button, Tabs } from '_components/_core';

import { GatewayActions, GatewayDetails, GatewayWebhooks } from './components';

import { AsaasValidationSchema, BancoInterValidationSchema } from './utilities';

import { StyledButton } from './styles';

const gatewayOptions = gateways => {
  return sortBy(
    gateways.map(gateway => ({
      value: gateway.id,
      label: gateway.name,
      mnemonic: gateway.mnemonic,
    })),
    'label',
  );
};

function GatewayForm({
  isOpen,
  gateway_id,
  gateway: oldGateway,

  title,

  onToggleForm,
  onBeforeSaveCallback,
  onAfterSaveCallback,
  onAfterSaveCallbackWithReset,

  onFetchCompanyPaymentGateway,
  onCreateCompanyPaymentGateway,
  onUpdateCompanyPaymentGateway,
  onDeleteCompanyPaymentGateway,

  onPerformCompanyPaymentGatewayAction,

  onFetchPaymentGatewayWebhooks,
  onCreatePaymentGatewayWebhook,
  onFetchPaymentGatewayWebhookEvents,

  availablePaymentGateways,
}) {
  const [isLoading, setIsLoading] = useState(false);
  const [newGateway, setNewGateway] = useState({});
  const [mnemonic, setMnemonic] = useState(null);
  const [activeTab, setActiveTab] = useState('MAIN');

  const companyGateway = useMemo(() => {
    if (gateway_id) {
      return newGateway;
    }

    return oldGateway;
  }, [gateway_id, newGateway, oldGateway]);

  useEffect(() => {
    if (!isOpen) {
      setActiveTab('MAIN');
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    if (!gateway_id) {
      return;
    }

    setIsLoading(true);

    onFetchCompanyPaymentGateway(gateway_id, found_gateway => {
      setNewGateway(found_gateway);

      setIsLoading(false);
    });
  }, [gateway_id, onFetchCompanyPaymentGateway, isOpen]);

  const initialValues = useMemo(() => {
    if (companyGateway) {
      return {
        id: companyGateway.id,
        payment_gateway_id: companyGateway.payment_gateway_id,
        mnemonic: companyGateway.mnemonic,
        access_token: companyGateway.access_token,
        client_id: companyGateway.client_id,
        client_secret: companyGateway.client_secret,
        certificate_crt: companyGateway.certificate_crt,
        certificate_key: companyGateway.certificate_key,
      };
    }

    return {
      id: null,
      payment_gateway_id: null,
      mnemonic: null,
      access_token: null,
      client_id: null,
      client_secret: null,
      certificate_crt: null,
      certificate_key: null,
    };
  }, [companyGateway]);

  const handleSubmitGateway = useCallback(
    (values, { resetForm }) => {
      setIsLoading(true);

      if (onBeforeSaveCallback) {
        onBeforeSaveCallback(values);
      }

      const errorCallback = () => {
        setIsLoading(false);
      };

      if (values.id) {
        onUpdateCompanyPaymentGateway(
          values.id,
          values,
          data => {
            if (onAfterSaveCallback) {
              onAfterSaveCallback(data);
            }

            if (onAfterSaveCallbackWithReset) {
              onAfterSaveCallbackWithReset(data);

              resetForm();
              onToggleForm();
            }

            if (!onBeforeSaveCallback && !onAfterSaveCallback) {
              resetForm();
              onToggleForm();
            }

            setIsLoading(false);
          },
          errorCallback,
        );
      } else {
        onCreateCompanyPaymentGateway(
          values,
          data => {
            if (onAfterSaveCallback) {
              onAfterSaveCallback(data);
            }

            if (onAfterSaveCallbackWithReset) {
              onAfterSaveCallbackWithReset(data);

              resetForm();
              onToggleForm();
            }

            if (!onBeforeSaveCallback && !onAfterSaveCallback) {
              resetForm();
              onToggleForm();
            }

            setIsLoading(false);
          },
          errorCallback,
        );
      }
    },
    [
      onCreateCompanyPaymentGateway,
      onUpdateCompanyPaymentGateway,
      onToggleForm,
      onBeforeSaveCallback,
      onAfterSaveCallback,
      onAfterSaveCallbackWithReset,
    ],
  );

  const handleDeleteGateway = useCallback(() => {
    if (companyGateway?.payment_gateway_id) {
      setIsLoading(true);

      onDeleteCompanyPaymentGateway(companyGateway.payment_gateway_id, () => {
        onToggleForm();

        setIsLoading(false);
      });
    }
  }, [companyGateway, onDeleteCompanyPaymentGateway, onToggleForm]);

  const renderFooter = useCallback(
    (handleSubmit, isValid) => (
      <div className="d-flex">
        <StyledButton
          variant="success"
          className="mr-2 flex-fill"
          isLoading={isLoading}
          disabled={!isValid}
          onClick={() => handleSubmit()}
          type="submit"
          id="btn-gateway-form-save"
        >
          Salvar Alterações
        </StyledButton>
        <Button
          id="btn-gateway-form-cancel"
          className="flex-fill"
          variant="inverse-dark"
          onClick={onToggleForm}
        >
          Cancelar
        </Button>
      </div>
    ),
    [onToggleForm, isLoading],
  );

  const renderDeleteButton = useCallback(() => {
    if (!companyGateway) return null;

    return (
      <div className="d-flex align-items-center justify-content-between mb-1 mt-1">
        <small className="ml-3 text-muted">
          {companyGateway?.created_at &&
            `Criado em: ${FORMATTERS.DATE_DDMMYYYYHHMM(companyGateway.created_at)}`}
        </small>
        <Button
          variant="link"
          className="text-danger"
          type="button"
          onClick={handleDeleteGateway}
          id="btn-delete-gateway"
        >
          Excluir
        </Button>
      </div>
    );
  }, [handleDeleteGateway, companyGateway]);

  const VALIDATION_SCHEMA = useMemo(() => {
    if (mnemonic === 'ASAAS' || mnemonic === 'ASAAS_SANDBOX') {
      return AsaasValidationSchema;
    }

    if (mnemonic === 'BANCO_INTER') {
      return BancoInterValidationSchema;
    }

    return null;
  }, [mnemonic]);

  const getTabs = useCallback(
    ({ values, setFieldValue, errors, touched, setMnemonic, gatewayOptions, isOpen }) => {
      const props = {
        values,
        setFieldValue,
        errors,
        touched,
        availablePaymentGateways,
        setMnemonic,
        gatewayOptions,
        isOpen,
        onFetchPaymentGatewayWebhooks,
        onFetchPaymentGatewayWebhookEvents,
        onCreatePaymentGatewayWebhook,
        onPerformCompanyPaymentGatewayAction,
      };

      const tabs = [
        {
          id: 'MAIN',
          title: 'Detalhes',
          content: activeTab === 'MAIN' && <GatewayDetails {...props} />,
        },
        {
          id: 'WEBHOOKS',
          title: 'Webhooks',
          content: activeTab === 'WEBHOOKS' && <GatewayWebhooks {...props} />,
        },
        {
          id: 'ACTIONS',
          title: 'Ações',
          content: activeTab === 'ACTIONS' && <GatewayActions {...props} />,
        },
      ];

      return tabs;
    },
    [
      isOpen,
      activeTab,
      availablePaymentGateways,
      onFetchPaymentGatewayWebhooks,
      onFetchPaymentGatewayWebhookEvents,
      onCreatePaymentGatewayWebhook,
      onPerformCompanyPaymentGatewayAction,
    ],
  );

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      onSubmit={handleSubmitGateway}
      validationSchema={VALIDATION_SCHEMA}
    >
      {({ handleSubmit, setFieldValue, values, isValid, errors, touched }) => (
        <FloatingCard
          title={title}
          fullHeight
          isVisible={isOpen}
          onToggleVisibility={onToggleForm}
          beforeFooterContent={renderDeleteButton()}
          footerContent={renderFooter(handleSubmit, isValid)}
          className={isOpen ? 'gateway-form-open' : 'gateway-form-closed'}
          bodyClassName="p-0"
          withCloseButton
          width={900}
        >
          <div className="p-3">
            <Tabs
              variant="pills"
              tabs={getTabs({
                values,
                setFieldValue,
                errors,
                touched,
                handleSubmit,
                setMnemonic,
                gatewayOptions,
                isOpen,
              })}
              activeTab={activeTab}
              onTabChange={tab => {
                setActiveTab(tab);
              }}
            />
          </div>
        </FloatingCard>
      )}
    </Formik>
  );
}

GatewayForm.defaultProps = {
  gateway: null,
  title: 'Gateway de Pagamento',
  paymentGateways: [],
  onDeleteCompanyPaymentGateway: () => {},
};

GatewayForm.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  gateway: PropTypes.object,
  onToggleForm: PropTypes.func.isRequired,
  onCreateCompanyPaymentGateway: PropTypes.func.isRequired,
  onUpdateCompanyPaymentGateway: PropTypes.func.isRequired,
  onDeleteCompanyPaymentGateway: PropTypes.func,
  title: PropTypes.string,
  adminPaymentGateways: PropTypes.array,
  paymentGateways: PropTypes.array,
};

export default GatewayForm;
